<template lang="pug">
  v-card
    v-card-title
      h2.card-title {{ 'modals.add_ingredient' | translate }}
    v-card-content
      v-type-select.mb-3(
        v-model="typeSearchText"
        :label="$t('base.type')"
        :hint="errorMessage('ingredient_type')"
        :invalid="!!errorMessage('ingredient_type')"
        :custom-types="filteredCustomTypes"
        :default-types="filteredDefaultTypes"
        @select="onTypeSelect"
        @create-enabled="onCreateTypeEnabled"
        @create-new="onCreateNew")
      v-input.mb-3(
        ref="nameInput"
        v-if="typeSearchText"
        :label="$t('base.name')"
        :hint="errorMessage('name')"
        :invalid="!!errorMessage('name')"
        v-model="name")
      v-select.mb-3(
        :label="$t('base.stock')"
        :options="filteredStocks"
        v-model="stock")
      .toogle-wrap
        ui-toggle-unit(
          :tolerancePercent="tolerance_percent"
          :toleranceUnit="tolerance_unit"
          :toleranceKg="tolerance_kg"
          @update="onToleranceUpdate")
      v-input.mb-3.mt-5(
        :label="$t('base.dry_metter')"
        :hint="errorMessage('dry_matter')"
        :invalid="!!errorMessage('dry_matter')"
        :float="true"
        type="text"
        pattern="^[0-9]+([.,][0-9]{0,2})?$"
        min="0"
        max="100"
        v-model="dry_matter")
      v-switch.mb-3(
        :label="$t('history.ignore_in_reports')"
        v-model="ignored_in_report")
      v-switch(
        :label="$t('history.autoload')"
        v-model="auto_load")
    v-card-actions
      v-btn.mr-2(
        :loading="loading"
        :disabled="!isValid"
        @click="onCreateIngredient")
        | {{ 'base.add' | translate }}
</template>

<script>
import api from '@/api'
import { mapActions, mapGetters } from 'vuex'
import formErrorsMixin from '@/mixins/form-errors.mixin.js'
import UiToggleUnit from '@/components/ui/ui-toggle-unit/UiToggleUnit.vue'

export default {
  name: 'CreateModal',

  components: {
    UiToggleUnit
  },

  mixins: [formErrorsMixin],

  data: () => ({
    name: null,
    tolerance_kg: 0,
    tolerance_percent: 97,
    tolerance_unit: 'percent',
    ignored_in_report: false,
    ingredient_type: null,
    stock: null,
    dry_matter: 0,
    auto_load: false,
    loading: false,
    errors: {
      name: null,
      ingredient_type: null
    },
    typeSearchText: '',
    selectedType: null,
    isDefaultType: false,
    createTypeEnabled: false,
    creatingNewType: false
  }),

  async mounted () {
    const company = +this.$route.params.company_id
    const farm = +this.$route.params.farm_id
    this.loadStocks({ company, farm })
    await this.loadIngredientTypes({ farm, limit: 2000 })
    await this.loadDefaultIngredientTypes()
  },

  computed: {
    ...mapGetters('stocks', ['stocks']),
    ...mapGetters('ingredienttypes', ['ingredientTypes', 'defaultIngredientTypes']),

    filteredStocks () {
      return this.stocks
    },

    filteredCustomTypes () {
      return this.ingredientTypes.filter(type => !type.deleted)
    },

    filteredDefaultTypes () {
      const customNames = this.ingredientTypes
        .filter(type => !type.deleted)
        .map(t => t.name.toLowerCase())

      return this.defaultIngredientTypes.filter(defaultType =>
        !customNames.includes(defaultType.name.toLowerCase())
      )
    },

    isValid () {
      return !!(this.name && this.stock && (this.ingredient_type || this.createTypeEnabled))
    }
  },

  methods: {
    ...mapActions('stocks', ['loadStocks']),
    ...mapActions('ingredienttypes', ['loadIngredientTypes', 'loadDefaultIngredientTypes', 'createIngredientType']),

    onTypeSelect ({ type, isDefault }) {
      this.selectedType = type
      this.isDefaultType = isDefault
      this.ingredient_type = type.id

      this.name = type.name

      this.createTypeEnabled = true
      this.errors.ingredient_type = null

      this.$nextTick(() => {
        if (this.$refs.nameInput && this.$refs.nameInput.$el) {
          const input = this.$refs.nameInput.$el.querySelector('input')
          if (input) {
            input.focus()
          }
        }
      })
    },

    onCreateTypeEnabled (enabled) {
      this.createTypeEnabled = enabled
      if (!enabled) {
        this.selectedType = null
        this.ingredient_type = null
        this.name = null
      }
      this.errors.ingredient_type = null
    },

    onCreateNew (typeName) {
      this.name = typeName

      this.$nextTick(() => {
        if (this.$refs.nameInput && this.$refs.nameInput.$el) {
          const input = this.$refs.nameInput.$el.querySelector('input')
          if (input) {
            input.focus()
          }
        }
      })
    },

    async onCreateIngredient () {
      this.loading = true

      if (this.dry_matter && this.dry_matter.includes(',')) {
        this.dry_matter = this.dry_matter.toString().replace(',', '.')
      }

      this.checkValidData()

      if (this.errors.name === null && this.errors.ingredient_type === null) {
        let typeId = this.ingredient_type

        if (!this.selectedType || this.isDefaultType) {
          this.creatingNewType = true
          const typePayload = {
            farm: +this.$route.params.farm_id,
            name: this.typeSearchText
          }
          const typeError = await this.createIngredientType(typePayload)

          if (typeError) {
            this.$toasted.error(this.$t('errors.incorrect_data'))
            this.loading = false
            this.creatingNewType = false
            return
          }

          const farm = +this.$route.params.farm_id
          await this.loadIngredientTypes({ farm })

          const newType = this.ingredientTypes.find(t =>
            t.name.toLowerCase() === this.typeSearchText.toLowerCase()
          )

          if (newType) {
            typeId = newType.id
          } else {
            this.$toasted.error(this.$t('errors.type_not_created'))
            this.loading = false
            this.creatingNewType = false
            return
          }

          this.creatingNewType = false
        }

        const payload = {
          name: this.name,
          farm: +this.$route.params.farm_id,
          dry_matter: +this.dry_matter,
          ignored_in_report: this.ignored_in_report,
          stock: { id: this.stock },
          ingredient_type: { id: typeId },
          auto_load: this.auto_load,
          tolerance_kg: this.tolerance_kg,
          tolerance_percent: this.tolerance_percent,
          tolerance_unit: this.tolerance_unit
        }

        const { errors } = await api.ingredients.create(payload)
        this.errors = errors

        if (errors) {
          if (errors.non_field_errors && errors.non_field_errors[0] === '5') {
            this.$toasted.error(this.$t('errors.incorrect_ingredient_type'))
          }
        } else {
          this.$emit('update', this.stock)
          this.$emit('close')
        }
      }

      this.loading = false
    },

    checkValidData () {
      this.errors.name = null
      this.errors.ingredient_type = null

      if (!this.name) {
        this.errors.name = ['3']
      } else if (this.name.length > 100) {
        this.errors.name = ['6', 100]
      }

      if (!this.typeSearchText) {
        this.errors.ingredient_type = ['3']
      }
    },

    onToleranceUpdate (payload) {
      this.tolerance_kg = payload.tolerance_kg
      this.tolerance_percent = payload.tolerance_percent
      this.tolerance_unit = payload.tolerance_unit
    }
  }
}
</script>